/**
 * Defines the FormContainer component, a customisable dialog wrapper that integrates with the application's form management system.
 * It provides a consistent layout for displaying forms within a dialog, including support for responsive design, dynamic content based on form submission state,
 * and custom styling using Material-UI components and the styled API.
 *
 */
import { DialogActions, Fade } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import * as React from 'react';
import { CloseButton } from '../buttons/CloseButton';
import { SubmitButton } from '../buttons/SubmitButton';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { SubmissionStatus } from '../../hooks/form';
import { forwardRef, useEffect } from 'react';
import { Stack } from '@mui/system';
import { FormSubmissionView } from './FormSubmissionView';

//slower fade-out transition for transparent submission view
const SubmissionViewFade = forwardRef((props, ref) => (
    <Fade {...props} ref={ref} timeout={{ enter: 400, exit: 800 }} />
));

SubmissionViewFade.displayName = 'SubmissionViewFade';

/**
 * A component that serves as a container for form dialogs. It includes a title, form content, and actions.
 * It can dynamically display content based on the submission state, optionally a preview page before the main form,
 * and adapts its layout for mobile screens.
 *
 * @component
 * @param {Object} props - The properties passed to the component
 * @param {string} props.formId - The user action type served by the form (from UserActionType)
 * @param {Object} props.formState - The current state of the form
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.handleClose - Function to close the dialog
 * @param {Element} props.title - The title of the form dialog
 * @param {React.ReactNode} props.children - Form content elements
 * @param {Function} onSubmissionViewClose - Function called when submission view is closed
 * @param {Element} props.submissionView - Element displayed upon successful form submission
 * @param {Array<string>} props.requiredFields - Fields required for form submission
 * @param {Function} props.handleSend - Function called when the form is submitted
 * @param {Object} props.submissionState - The current state of the form's submission.
 * @param {Function} props.resetFields - A function for resetting all input fields in the form.
 * @param {Element} props.previewPage - Content displayed initially when form opened
 * @param {boolean} props.previewPageOpen - State to track whether a provided preview page is open
 * @param {Function} enableSubmitButton - Optional function to control whether submit button is enabled/disabled
 * @param {Boolean} disableStandardSubmissionView - true to disable standard transparent submission view
 * @param {Boolean} hideCloseButton - bool used to hide top-right close button
 * @param {String} dataAnalyticsId - Tag for analytics
 * @returns {JSX.Element} The styled dialog component with form content
 */
export const FormContainer = ({
    formId,
    formState,
    open,
    handleClose,
    title,
    children,
    submissionView,
    onSubmissionViewClose,
    requiredFields,
    handleSend,
    submissionState,
    resetFields,
    previewPage,
    previewPageOpen,
    enableSubmitButton,
    disableStandardSubmissionView,
    hideCloseButton,
    dataAnalyticsId,
    ...props
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    /** Called when form first opened to reset fields. */
    useEffect(() => {
        if (open) {
            resetFields();
        }
    }, [open]);

    /**
     * Determines if a message about mandatory fields should be displayed,
     * based on the form state and required fields.
     *
     * @returns {boolean} True if the message should be displayed, otherwise false.
     */
    function shouldDisplayMandatoryMessage() {
        return (
            requiredFields.length > 1 &&
            requiredFields.some((field) => formState[field].error)
        );
    }

    /**
     * Prevent forms closing when background clicked, retains close using Esc key.
     * For Submission View we retain close on background click by checking the submission state.
     */
    function handleFormClose(event, reason) {
        if (
            (reason && reason !== 'backdropClick') ||
            submissionState.status === SubmissionStatus.SUCCESS ||
            previewPageOpen
        ) {
            handleClose();
        }
    }

    /**
     * Returns whether all required fields of the form have been populated. For any required
     * phone number fields we check to make sure the value is not just a country code.
     * @returns {boolean} True if all required fields are populated
     */
    function allRequiredFieldsPopulated() {
        return requiredFields.every((field) => {
            if (field === 'phoneNumber') {
                return (
                    formState[field].value !==
                    '+' + formState[field].countryCode
                );
            }
            return formState[field].value !== '';
        });
    }

    /**
     * Logic to automatically close the standard form submission view two seconds after successful send
     * @returns {Promise<void>}
     */
    async function handleSubmitAndClose() {
        try {
            const valid = await handleSend();

            !disableStandardSubmissionView &&
                valid &&
                setTimeout(() => {
                    onSubmissionViewClose
                        ? onSubmissionViewClose()
                        : handleClose();
                }, 2000);
        } catch (submissionError) {
            console.error('Form submission failed:', submissionError);
        }
    }

    return (
        <Dialog
            onClose={handleFormClose}
            open={open}
            fullScreen={isMobile}
            aria-labelledby={formId}
            TransitionComponent={
                !disableStandardSubmissionView &&
                submissionState.status === SubmissionStatus.SUCCESS
                    ? SubmissionViewFade
                    : Fade
            }
            PaperProps={
                !disableStandardSubmissionView &&
                submissionState.status === SubmissionStatus.SUCCESS
                    ? {
                          style: {
                              background: 'transparent',
                              boxShadow: 'none',
                              justifyContent: 'center'
                          }
                      }
                    : {}
            }
            data-analyticsid={dataAnalyticsId}
            {...props}
        >
            <>
                {previewPageOpen ? (
                    <>{previewPage}</>
                ) : submissionState.status === SubmissionStatus.SUCCESS ? (
                    disableStandardSubmissionView ? (
                        <>{submissionView}</>
                    ) : (
                        <FormSubmissionView text={submissionView} />
                    )
                ) : (
                    <>
                        <DialogTitle sx={{ p: 3, pb: 2, pr: 2 }}>
                            <Stack
                                direction='row'
                                alignItems='center'
                                justifyContent='space-between'
                            >
                                {title}
                                {!hideCloseButton && (
                                    <CloseButton
                                        onClose={handleClose}
                                        color={theme.palette.common.black}
                                    />
                                )}
                            </Stack>
                        </DialogTitle>
                        <DialogContent sx={{ paddingBottom: '24px' }}>
                            {children}
                        </DialogContent>
                        <DialogActions
                            style={{
                                minHeight: '8vh',
                                padding: 24
                            }}
                        >
                            {shouldDisplayMandatoryMessage() && (
                                <Typography
                                    variant='smallprint'
                                    color={theme.palette.errorText}
                                    sx={{
                                        margin: 'auto'
                                    }}
                                >
                                    Please complete all mandatory fields
                                </Typography>
                            )}
                            {/*Typography element below to display any api error message*/}
                            <Typography
                                variant='smallprint'
                                align={'center'}
                                color={theme.palette.errorText}
                                sx={{
                                    paddingLeft: 3,
                                    paddingRight: 3,
                                    display: 'flex'
                                }}
                            >
                                {submissionState.message}
                            </Typography>
                            {/*Save Button only rendered when submission status is not in_progress */}
                            {submissionState.status !==
                                SubmissionStatus.IN_PROGRESS && (
                                <SubmitButton
                                    onClick={handleSubmitAndClose}
                                    //submit button enabled based on provided function or if all required fields populated
                                    disabled={
                                        enableSubmitButton
                                            ? !enableSubmitButton()
                                            : !allRequiredFieldsPopulated()
                                    }
                                />
                            )}
                        </DialogActions>
                    </>
                )}
            </>
        </Dialog>
    );
};

FormContainer.propTypes = {
    formId: PropTypes.string,
    formState: PropTypes.object,
    open: PropTypes.bool,
    handleClose: PropTypes.func,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    children: PropTypes.node,
    submissionView: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    onSubmissionViewClose: PropTypes.func,
    requiredFields: PropTypes.array,
    handleSend: PropTypes.func,
    submissionState: PropTypes.object,
    resetFields: PropTypes.func,
    previewPage: PropTypes.element,
    previewPageOpen: PropTypes.bool,
    enableSubmitButton: PropTypes.func,
    disableStandardSubmissionView: PropTypes.bool,
    hideCloseButton: PropTypes.bool,
    dataAnalyticsId: PropTypes.string
};
